/**
 * SANAL HESAP PAYLAŞIMI (kullanıcı kararı 19.10.2026).
 *
 * Paylaşılan şey KURALDIR (tohum dahil), hesabın adı ve rengi; sonuç
 * paylaşılmaz — alıcı kendi cihazında aynı motorla hesaplar ve aynı sonucu
 * görür. Üç taşıyıcı, tek içerik:
 *  - link: kural adresin "#" sonrasında (`#/sanal-hesaplar?kural=…`).
 *    Tarayıcı "#" sonrasını sunucuya HİÇ göndermez; link süresizdir;
 *  - QR: aynı link;
 *  - dosya: aynı içerik (`{ v, kural }` JSON'u).
 *
 * SÜRÜM: içerik bir biçim sürümü (`v`) taşır. Bu uygulama `PAYLASIM_SURUMU`
 * ve öncesini okur. Daha yeni sürüm ya da TANINMAYAN bir alan varsa kural
 * eklenmez ve sebebi söylenir: alanı sessizce atlamak, kuralın sonucunu
 * fark ettirmeden değiştirirdi. Yapı burada kapalı listelere göre denetlenir;
 * değerlerin anlamını (oran toplamı, tarih sırası…) kaydederken motor
 * denetler (`sanalTanimDogrula`).
 *
 * GERÇEK HESAP BİLGİSİ GİTMEZ: para akışı gerçek hesaplardan alınan (B)
 * kuralda paylaşanın hesapları ve para girişleri içeriğe hiç girmez; yalnızca
 * "para akışı gerçek hesaplardan" bilgisi gider. Alıcı akışı kendi
 * hesaplarına bağlar ya da düzenli akışa geçer.
 */

import { CURRENCIES } from "@parafon/contracts";
import {
  HESAP_RENKLERI,
  SANAL_DENGELEMELERI,
  SANAL_PERIYOT_BIRIMLERI,
  SANAL_TEMETTU_KULLANIMLARI,
} from "@parafon/contracts/local-api";
import type { SanalHesapGirdisi, SanalHesapTanimi } from "@parafon/contracts/local-api";
import { routeHref } from "./router";
import { M } from "../dil";

/** Bu uygulamanın yazdığı ve okuyabildiği en yeni biçim. */
export const PAYLASIM_SURUMU = 1;
/** Linkte kuralın taşındığı parametre. */
export const PAYLASIM_PARAMETRESI = "kural";
/** Dosyanın uzantısı (içerik JSON). */
export const PAYLASIM_DOSYA_UZANTISI = ".parafon-sanal.json";

/**
 * Paylaşılan kural: `SanalHesapGirdisi`nin kimliksiz hâli; B akışında
 * hesap kimlikleri YOK.
 */
export type PaylasilanKural = Omit<SanalHesapGirdisi, "id" | "paraAkisi" | "tohum"> & {
  readonly tohum: number;
  readonly paraAkisi: Exclude<SanalHesapGirdisi["paraAkisi"], { tur: "defter" }> | { readonly tur: "defter" };
};

export class PaylasimHatasi extends Error {}

const YENI_SURUM = M.sanal.okuma.yeniSurum;

// ---------------------------------------------------------------------------
// Yazma
// ---------------------------------------------------------------------------

export function kuraliPaylasimaCevir(t: SanalHesapTanimi): PaylasilanKural {
  // Kimlik gitmez (alıcıda yeni hesap); B akışında hesaplar gitmez.
  const { id: _id, paraAkisi, ...geri } = t;
  return { ...geri, paraAkisi: paraAkisi.tur === "defter" ? { tur: "defter" } : paraAkisi };
}

export function paylasimMetni(t: SanalHesapTanimi): string {
  return JSON.stringify({ v: PAYLASIM_SURUMU, kural: kuraliPaylasimaCevir(t) });
}

function base64UrlYaz(metin: string): string {
  const bayt = new TextEncoder().encode(metin);
  let ikili = "";
  for (const b of bayt) ikili += String.fromCharCode(b);
  return btoa(ikili).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64UrlOku(metin: string): string {
  if (!/^[A-Za-z0-9_-]*$/.test(metin)) throw new PaylasimHatasi(M.sanal.okuma.linkBozuk);
  const dolgulu = metin.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((metin.length + 3) % 4);
  let ikili: string;
  try {
    ikili = atob(dolgulu);
  } catch {
    throw new PaylasimHatasi(M.sanal.okuma.linkBozuk);
  }
  const bayt = Uint8Array.from(ikili, (c) => c.charCodeAt(0));
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bayt);
  } catch {
    throw new PaylasimHatasi(M.sanal.okuma.linkBozuk);
  }
}

/** Uygulamanın adresi + "#" sonrasında kural. Sunucuya giden kısım değişmez. */
export function paylasimLinki(t: SanalHesapTanimi, taban: string = varsayilanTaban()): string {
  return `${taban}${routeHref("sanal-hesaplar")}?${PAYLASIM_PARAMETRESI}=${base64UrlYaz(paylasimMetni(t))}`;
}

function varsayilanTaban(): string {
  const l = globalThis.location;
  return l === undefined ? "" : `${l.origin}${l.pathname}`;
}

export function paylasimDosyaAdi(t: SanalHesapTanimi): string {
  const sade = t.ad
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/ı/g, "i")
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  return `${sade === "" ? "sanal-hesap" : sade}${PAYLASIM_DOSYA_UZANTISI}`;
}

// ---------------------------------------------------------------------------
// Okuma — yapı kapalı listelere göre; tanınmayan alan = eklenmez
// ---------------------------------------------------------------------------

type Nesne = Readonly<Record<string, unknown>>;

function nesne(v: unknown, yer: string): Nesne {
  if (typeof v !== "object" || v === null || Array.isArray(v)) {
    throw new PaylasimHatasi(M.sanal.okuma.bicimDegil(yer));
  }
  return v as Nesne;
}

/** Yalnızca bilinen alanlar; zorunlular eksiksiz. */
function alanlar(o: Nesne, yer: string, zorunlu: readonly string[], istege: readonly string[] = []): void {
  for (const k of Object.keys(o)) {
    if (!zorunlu.includes(k) && !istege.includes(k)) {
      throw new PaylasimHatasi(M.sanal.okuma.taninmayanAlan(yer === "" ? k : `${yer}.${k}`, YENI_SURUM));
    }
  }
  for (const k of zorunlu) {
    if (!(k in o)) throw new PaylasimHatasi(M.sanal.okuma.eksik(yer === "" ? k : `${yer}.${k}`));
  }
}

function metin(v: unknown, yer: string): string {
  if (typeof v !== "string") throw new PaylasimHatasi(M.sanal.okuma.metinOlmali(yer));
  return v;
}

function tamSayi(v: unknown, yer: string): number {
  if (typeof v !== "number" || !Number.isInteger(v)) throw new PaylasimHatasi(M.sanal.okuma.tamSayiOlmali(yer));
  return v;
}

function bayrak(v: unknown, yer: string): boolean {
  if (typeof v !== "boolean") throw new PaylasimHatasi(M.sanal.okuma.bayrakOlmali(yer));
  return v;
}

/** Kapalı listeden; listede olmayan değer de daha yeni bir sürümün işaretidir. */
function liste<T extends string>(v: unknown, kapali: readonly T[], yer: string): T {
  if (typeof v !== "string") throw new PaylasimHatasi(M.sanal.okuma.metinOlmali(yer));
  if (!(kapali as readonly string[]).includes(v)) {
    throw new PaylasimHatasi(M.sanal.okuma.taninmayanDeger(yer, v, YENI_SURUM));
  }
  return v as T;
}

const PARA_KODLARI = CURRENCIES.map((c) => c.code);

function istegeBagli<T>(o: Nesne, k: string, oku: (v: unknown, yer: string) => T): { [key: string]: T } {
  const v = o[k];
  return v === undefined || v === null ? {} : { [k]: oku(v, k) };
}

function kuralOku(raw: unknown): PaylasilanKural {
  const o = nesne(raw, "kural");
  alanlar(
    o,
    "",
    ["ad", "paraBirimi", "varliklar", "baslangic", "paraAkisi", "komisyonOrani", "temettu", "dengeleme", "kayma", "tohum"],
    [
      "renk",
      "bitis",
      "komisyonSabit",
      "komisyonEnAz",
      "komisyonEnCok",
      "komisyonParaBirimi",
      "temettuKomisyonu",
      "artanSonrakiAlima",
      "dengelemeEsigi",
    ],
  );
  const vRaw = o["varliklar"];
  if (!Array.isArray(vRaw)) throw new PaylasimHatasi(M.sanal.okuma.varliklarListe);
  const varliklar = vRaw.map((v, i) => {
    const yer = `varliklar[${i}]`;
    const vo = nesne(v, yer);
    alanlar(vo, yer, ["instrumentId", "oran"], ["kesirli", "kilitli"]);
    return {
      instrumentId: metin(vo["instrumentId"], `${yer}.instrumentId`),
      oran: metin(vo["oran"], `${yer}.oran`),
      ...istegeBagli(vo, "kesirli", (x, y) => bayrak(x, `${yer}.${y}`)),
      ...istegeBagli(vo, "kilitli", (x, y) => bayrak(x, `${yer}.${y}`)),
    };
  });

  const pa = nesne(o["paraAkisi"], "paraAkisi");
  const tur = liste(pa["tur"], ["duzenli", "defter"] as const, "paraAkisi.tur");
  let paraAkisi: PaylasilanKural["paraAkisi"];
  if (tur === "defter") {
    // B akışında hesap kimlikleri asla gelmez; gelirse içerik bizim değil.
    alanlar(pa, "paraAkisi", ["tur"]);
    paraAkisi = { tur: "defter" };
  } else {
    alanlar(pa, "paraAkisi", ["tur", "periyot", "periyotTutari"], ["baslangicTutari", "cekim"]);
    const per = nesne(pa["periyot"], "paraAkisi.periyot");
    alanlar(per, "paraAkisi.periyot", ["birim", "adim"]);
    const cekimRaw = pa["cekim"];
    let cekim: { tutar: string; baslangic: string } | null = null;
    if (cekimRaw !== undefined && cekimRaw !== null) {
      const co = nesne(cekimRaw, "paraAkisi.cekim");
      alanlar(co, "paraAkisi.cekim", ["tutar", "baslangic"]);
      cekim = { tutar: metin(co["tutar"], "cekim.tutar"), baslangic: metin(co["baslangic"], "cekim.baslangic") };
    }
    paraAkisi = {
      tur: "duzenli",
      periyot: {
        birim: liste(per["birim"], SANAL_PERIYOT_BIRIMLERI, "periyot.birim"),
        adim: tamSayi(per["adim"], "periyot.adim"),
      },
      periyotTutari: metin(pa["periyotTutari"], "periyotTutari"),
      ...istegeBagli(pa, "baslangicTutari", metin),
      ...(cekim === null ? {} : { cekim }),
    };
  }

  const ko = nesne(o["kayma"], "kayma");
  alanlar(ko, "kayma", ["geri", "ileri"]);

  return {
    ad: metin(o["ad"], "ad"),
    ...istegeBagli(o, "renk", (x, y) => liste(x, HESAP_RENKLERI, y)),
    paraBirimi: liste(o["paraBirimi"], PARA_KODLARI, "paraBirimi"),
    varliklar,
    baslangic: metin(o["baslangic"], "baslangic"),
    ...istegeBagli(o, "bitis", metin),
    paraAkisi,
    komisyonOrani: metin(o["komisyonOrani"], "komisyonOrani"),
    ...istegeBagli(o, "komisyonSabit", metin),
    ...istegeBagli(o, "komisyonEnAz", metin),
    ...istegeBagli(o, "komisyonEnCok", metin),
    ...istegeBagli(o, "komisyonParaBirimi", (x, y) => liste(x, PARA_KODLARI, y)),
    ...istegeBagli(o, "temettuKomisyonu", bayrak),
    ...istegeBagli(o, "artanSonrakiAlima", bayrak),
    temettu: liste(o["temettu"], SANAL_TEMETTU_KULLANIMLARI, "temettu"),
    dengeleme: liste(o["dengeleme"], SANAL_DENGELEMELERI, "dengeleme"),
    ...istegeBagli(o, "dengelemeEsigi", metin),
    kayma: { geri: tamSayi(ko["geri"], "kayma.geri"), ileri: tamSayi(ko["ileri"], "kayma.ileri") },
    tohum: tamSayi(o["tohum"], "tohum"),
  } as PaylasilanKural;
}

/** Dosyanın ya da linkin içindeki JSON metni → kural. */
export function paylasimiOku(json: string): PaylasilanKural {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    throw new PaylasimHatasi(M.sanal.okuma.paylasimDegil);
  }
  const o = nesne(raw, M.sanal.okuma.yerPaylasim);
  const v = o["v"];
  if (typeof v !== "number" || !Number.isInteger(v) || v < 1) {
    throw new PaylasimHatasi(M.sanal.okuma.paylasimDegil);
  }
  // Sürüm, alanlardan ÖNCE: yeni bir sürümün alanları zaten tanınmaz.
  if (v > PAYLASIM_SURUMU) throw new PaylasimHatasi(YENI_SURUM);
  alanlar(o, "", ["v", "kural"]);
  return kuralOku(o["kural"]);
}

/** Linkin "#" sonrasındaki parametre değeri → kural. */
export function linkParametresiniOku(deger: string): PaylasilanKural {
  return paylasimiOku(base64UrlOku(deger));
}

/**
 * Alıcının kasasında aynı ad varsa sonuna numara: "Ad (2)", "Ad (3)"…
 * Ad sınırı (60) aşılmasın diye gerekirse kökten kısaltılır.
 */
export function benzersizAd(ad: string, mevcut: readonly string[]): string {
  const kok = ad.trim();
  const var_ = new Set(mevcut.map((a) => a.trim()));
  if (!var_.has(kok)) return kok;
  for (let n = 2; ; n += 1) {
    const ek = ` (${n})`;
    const aday = `${kok.slice(0, 60 - ek.length)}${ek}`;
    if (!var_.has(aday)) return aday;
  }
}

// ---------------------------------------------------------------------------
// Açılışta yakalama — kural kilit ekranından sağ çıksın
// ---------------------------------------------------------------------------

/**
 * Link açıldığında kasa kilitli olabilir: tanıtım → kilit → açılış sayfası
 * kuralı adresi değiştirir ve kural kaybolurdu. Bu yüzden kural uygulama
 * kalkarken adresten BELLEĞE alınır ve adresten silinir (geçmişte ya da
 * ekranda kalmasın); kilit açılınca Sanal Hesaplar'da onay sorulur. Bu
 * onay açılış sayfası kuralından önce gelir.
 */
let bekleyen: string | null = null;

export function paylasimiYakala(hash: string = globalThis.location?.hash ?? ""): boolean {
  const i = hash.indexOf("?");
  if (i < 0) return false;
  const deger = new URLSearchParams(hash.slice(i + 1)).get(PAYLASIM_PARAMETRESI);
  if (deger === null) return false;
  bekleyen = deger;
  try {
    globalThis.history?.replaceState(globalThis.history.state, "", routeHref("sanal-hesaplar"));
  } catch {
    // replaceState yoksa adres kalır; kural yine bellekte.
  }
  return true;
}

export function bekleyenPaylasimVar(): boolean {
  return bekleyen !== null;
}

/** Bekleyen linki bir kez verir. */
export function bekleyenPaylasimiAl(): string | null {
  const d = bekleyen;
  bekleyen = null;
  return d;
}
